import { Flex, useAssetTranslation } from '@mss/mss-component-library';
import { Select, Input } from 'antd';
import type { SelectProps } from 'antd/es/select';
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';

type FieldKey = 'name' | 'location';
//TODO: use API for name/location selection value.
const FIELD_CONFIG: Record<FieldKey, {
  label: string;
  placeholder: string;
  options: { label: string; value: string }[];
}> = {
  name: {
    label: 'Asset Name',
    placeholder: 'Select Asset Name source',
    options: [
      { label: 'Descriptor', value: 'descriptor' },
      { label: 'Device ID', value: 'device_id' },
      { label: 'Device Tag', value: 'device_tag' }
    ]
  },
  location: {
    label: 'Location',
    placeholder: 'Select Location source',
    options: [
      { label: 'Device Path', value: 'device_path' },
      { label: 'PRM Location', value: 'location' },
    ]
  }
};
//TODO: use API for name/location selection value.
const FIELD_OPTIONS: { label: string; value: FieldKey }[] = [
  { label: 'Asset Name', value: 'name' },
  { label: 'Location', value: 'location' },
];

type PreFillSettingsProps = {
  assetMap: Record<string | number, {
    asset_data: Record<string, any>;
    [key: string]: any;
  }>;
  onStatusChange: (isComplete: boolean) => void;
};

export const PreFillSettings = ({
  assetMap,
  onStatusChange,
}: PreFillSettingsProps) => {
  const { t } = useAssetTranslation();
  const { values, setValues } = useFormikContext<any>();
  const assetKeys = useMemo(() => Object.keys(assetMap || {}), [JSON.stringify(assetMap)]);

  const defaultRules = useMemo(() => {
    if (assetKeys.length === 0 || !values?.assets) return {};
    const firstAssetKey = assetKeys[0];
    const firstAsset = values.assets[firstAssetKey];
    return firstAsset?.preFillRules ? { ...firstAsset.preFillRules } : {};
  }, [assetKeys, JSON.stringify(values?.assets)]);


  const [selectedFields, setSelectedFields] = useState<Partial<Record<FieldKey, string>>>(defaultRules);
  const [activeFields, setActiveFields] = useState<FieldKey[]>(
    Object.keys(defaultRules).filter(key => ['name', 'location'].includes(key)) as FieldKey[]
  );

  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const assetMapRef = useRef(assetMap);
  useEffect(() => {
    assetMapRef.current = assetMap;
  }, [assetMap]);


  const batchUpdateAssets = useCallback(debounce((fields: Partial<Record<FieldKey, string>>) => {
    if (assetKeys.length === 0 || !setValues) return;

    setTimeout(() => {
      const currentValues = valuesRef.current;
      const currentAssetMap = assetMapRef.current;
      const newValues = { ...currentValues };
      if (!newValues.assets) newValues.assets = {};

      let hasChange = false;
      assetKeys.forEach(assetKey => {
        const asset = newValues.assets[assetKey] || {};
        const currentRules = asset.preFillRules || {};
        const currentValuesObj = asset.values || {};

        if (JSON.stringify(currentRules) !== JSON.stringify(fields)) {
          hasChange = true;
          newValues.assets[assetKey] = { ...asset, preFillRules: { ...fields } };
        }

        if (!newValues.assets[assetKey].values) newValues.assets[assetKey].values = {};

        Object.entries(fields).forEach(([fieldKey, sourceKey]) => {
          const formFieldName = fieldKey as FieldKey;

          if (!sourceKey) {
            if (currentValuesObj[formFieldName] !== '') {
              hasChange = true;
              newValues.assets[assetKey].values[formFieldName] = '';
            }
            return;
          }

          const sourceValue = currentAssetMap[assetKey]?.asset_data?.[sourceKey] || '';
          let finalValue = sourceValue;
          if (formFieldName === 'location' && finalValue) {
            finalValue = finalValue.replace(/\\/g, '\\\\');
          }
          
          if (currentValuesObj[formFieldName] !== finalValue) {
            hasChange = true;
            newValues.assets[assetKey].values[formFieldName] = finalValue;
          }
        });

        ['name', 'location'].forEach(fieldKey => {
          const formFieldName = fieldKey as FieldKey;
          if (!fields[formFieldName] && currentValuesObj[formFieldName] !== '') {
            hasChange = true;
            newValues.assets[assetKey].values[formFieldName] = '';
          }
        });
      });

      if (hasChange) {
        setValues(newValues);
      }
    }, 0);
  }, 50), [assetKeys, setValues]);


  useEffect(() => {
    return () => {
      batchUpdateAssets.cancel();
    };
  }, [batchUpdateAssets]);


  useEffect(() => {
    batchUpdateAssets(selectedFields);
  }, [selectedFields, batchUpdateAssets]);


  const handleFieldSelect: SelectProps<FieldKey[]>['onChange'] = (values) => {
    const fieldValues = Array.isArray(values) ? values : [];
    setActiveFields(fieldValues);


    const newSelectedFields = { ...selectedFields };
    Object.keys(newSelectedFields).forEach(key => {
      if (!fieldValues.includes(key as FieldKey)) {
        delete newSelectedFields[key as FieldKey];
      }
    });
    setSelectedFields(newSelectedFields);
  };


  const handleDataSourceChange = (field: FieldKey, value: string) => {
    setSelectedFields(prev => ({ ...prev, [field]: value }));
  };


  const checkPreFillComplete = useCallback(() => {

    const isComplete = activeFields.length === 0 || activeFields.every(fieldKey => !!selectedFields[fieldKey]);
    onStatusChange(isComplete);
    return isComplete;
  }, [activeFields, selectedFields, onStatusChange]);


  useEffect(() => {
    checkPreFillComplete();
  }, [activeFields, selectedFields, checkPreFillComplete]);

  return (
    <div className="p-4">
      <h3>{t('Select prefilled fields from MSS database')}</h3>

      <Flex
        column={false}
        h="start"
        className="my-4 gap-6"
      >
        <div className="w-1/4">
          <Select<FieldKey[]>
            mode="multiple"
            value={activeFields}
            onChange={handleFieldSelect}
            options={FIELD_OPTIONS.map(opt => ({
              label: t(opt.label),
              value: opt.value
            }))}
            placeholder={t('Select fields to pre-fill')}
            className="mt-2"
            style={{ width: '100%' }}
            maxTagCount={2}
            data-ta="preFillSettingsFields"
          />
        </div>

        <div className="w-3/4">
          <Flex h="between" className="mb-2">
            <div className="w-1/2 text-base font-bold pl-20">
              {t('Selected fields')}
            </div>
            <div className="w-1/2 text-base font-bold">
              {t('Value')}
            </div>
          </Flex>

          <div>
            {activeFields.map(fieldKey => (
              <Flex
                key={fieldKey}
                h="center"
                className="mb-4"
              >
                <div className="w-1/2 pr-4 pl-20">
                  <Input
                    value={t(FIELD_CONFIG[fieldKey].label)}
                    disabled={true}
                    style={{
                      padding: '4px',
                      background: 'transparent',
                      border: 'none',
                      color: '#333',
                      outline: 'none'
                    }}
                    data-ta={`preFillSettingsField_${fieldKey}`}
                  />
                </div>
                <div className="w-1/2">
                  <Select<string>
                    value={selectedFields[fieldKey] || ''}
                    onChange={(value) => handleDataSourceChange(fieldKey, value || '')}
                    options={FIELD_CONFIG[fieldKey].options.map(opt => ({
                      label: t(opt.label),
                      value: opt.value
                    }))}
                    placeholder={t(FIELD_CONFIG[fieldKey].placeholder)}
                    style={{ width: '100%' }}
                    allowClear={true}
                    onClear={() => handleDataSourceChange(fieldKey, '')}
                    data-ta={`preFillSettingsField_${fieldKey}`}
                  />
                </div>
              </Flex>
            ))}
          </div>
        </div>
      </Flex>
    </div>
  );
};
